import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';

function EyeIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function AcceptInvite({ onLoginSuccess }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('acceptInvite.missingToken'));
      setLoadingInfo(false);
      return;
    }
    const fetchInvite = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/team/invite/${token}`);
        setInviteInfo(response.data.data);
      } catch (err) {
        setError(t('acceptInvite.invalidInvite'));
      } finally {
        setLoadingInfo(false);
      }
    };
    fetchInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const inputClass = 'w-full bg-surfaceAlt border border-edge rounded-lg px-3 py-2.5 pr-10 text-white text-sm box-border focus:outline-none focus:border-accent';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const strongEnough = password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password);
    if (!strongEnough) {
      setError(t('register.passwordRequirementsError'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/v1/team/accept-invite`, { token, password });
      onLoginSuccess(response.data.access_token);
      navigate('/dashboard');
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('register.genericError'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="bg-panel min-h-screen flex items-center justify-center px-4 font-sans">
      <div className="bg-surface border border-edge rounded-2xl p-8 w-full max-w-sm">
        <h1 className="text-accent text-xl font-extrabold mb-1">{t('common.appName')}</h1>

        {loadingInfo ? (
          <p className="text-muted text-sm">{t('dashboard.loading')}</p>
        ) : inviteInfo ? (
          <>
            <p className="text-muted text-sm mb-1">{t('acceptInvite.subtitle', { tenant: inviteInfo.tenant_name })}</p>
            <p className="text-muted text-xs mb-6">{t('acceptInvite.joiningAs', { email: inviteInfo.email })}</p>

            {error && (
              <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="text-muted text-xs block mb-1.5">{t('resetPassword.newPassword')}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                    tabIndex={-1}
                  >
                    <EyeIcon visible={showPassword} />
                  </button>
                </div>
                <p className="text-muted text-[11px] mt-1.5">{t('register.passwordHint')}</p>
              </div>
              <div>
                <label className="text-muted text-xs block mb-1.5">{t('register.confirmPassword')}</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted cursor-pointer"
                    tabIndex={-1}
                  >
                    <EyeIcon visible={showConfirmPassword} />
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`bg-accent text-panel font-bold text-sm px-4 py-2.5 rounded-lg cursor-pointer ${loading ? 'opacity-70' : ''}`}
              >
                {loading ? t('acceptInvite.submitting') : t('acceptInvite.submitButton')}
              </button>
            </form>
          </>
        ) : (
          <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default AcceptInvite;